import { useState } from 'react'
import { Link, useParams, useSearchParams } from 'react-router-dom'
import {
  Activity, ArrowLeft, KeyRound, ListTree, Loader2, Plus, Settings2, ShieldCheck,
} from 'lucide-react'
import {
  ErpTable, KpiStrip, MetricCard, Panel, SectionHeader, Td, Th,
} from '@/components/erp/primitives'
import { Modal } from '@/shared/ui/Modal'
import { CursorPager } from '@/shared/ui/CursorPager'
import { EmptyState, ErrorStrip, Forbidden, LoadingRows, Note } from '@/shared/ui/states'
import { useLoad } from '@/features/administration/hooks'
import { useCan } from '@/shared/auth/session'
import { explain } from '@/shared/api/problem'
import { fmtDateTime, fmtNumber, humanise } from '@/shared/lib/format'
import { cn } from '@/lib/utils'
import type {
  CredentialResponse, HealthCheckResponse, IntegrationCallResponse, ProviderResponse,
} from '@/shared/api/types'
import {
  AUTH_KIND_LABEL, CATEGORY_LABEL, CREDENTIAL_KIND_LABEL,
  disableIntegration, enableIntegration, getIntegration, integrationCalls, integrationReport,
  listCredentials, listProviders, promoteCredential, putCredential, revokeCredential,
  runHealthCheck, testCredential, testCredentialsLocally,
} from './api'
import { PROVIDER_NOT_WIRED, providerLabel } from './providers'
import { EditRegistrationButton, EditUnavailableNote } from './EditRegistration'
import { integrationReportModel, outcomeTone } from './report'
import { KnownIssue, RangePicker, ReportBody, rangeParams, type RangeId } from './ReportPanels'
import { DatePicker } from '@/components/erp/date-picker'

const TABS = [
  { id: 'overview', label: 'Overview', icon: Settings2 },
  { id: 'credentials', label: 'Credentials', icon: KeyRound },
  { id: 'report', label: 'Report', icon: Activity },
  { id: 'calls', label: 'Calls', icon: ListTree },
] as const

const CRED_STATUS_STYLE: Record<string, string> = {
  ACTIVE: 'text-emerald-700 dark:text-emerald-400',
  ROTATING: 'text-amber-700 dark:text-amber-400',
  RETIRED: 'text-muted-foreground',
  REVOKED: 'text-red-700 dark:text-red-400',
  EXPIRED: 'text-red-700 dark:text-red-400',
}

/**
 * One integration, in full.
 *
 * Replaces the old detail drawer. A drawer was right when an integration was a
 * row with a credential list; it is the wrong container for configuration plus a
 * rotation lifecycle plus charts plus a paged call log, all of which want width
 * and a URL you can send to someone.
 *
 * The two things that must stay visibly separate on this page are **config** and
 * **credentials**. They are different endpoints, different permissions and
 * different blast radii: editing a config key is reversible and visible, while
 * promoting a credential changes what the system authenticates as. They get
 * separate tabs for that reason, not for tidiness.
 */
export function IntegrationDetailPage() {
  const { id = '' } = useParams()
  const canRead = useCan('integration:read')
  const canUpdate = useCan('integration:update')
  const canCredential = useCan('integration:credential')

  const [params, setParams] = useSearchParams()
  const rawTab = params.get('tab')
  const tab = TABS.some((t) => t.id === rawTab) ? (rawTab as (typeof TABS)[number]['id']) : 'overview'
  const setTab = (t: string) => {
    const sp = new URLSearchParams(params)
    sp.set('tab', t)
    setParams(sp, { replace: true })
  }

  const detail = useLoad(() => getIntegration(id), [id])
  const providers = useLoad(() => listProviders(), [])
  const i = detail.data
  const descriptor = providers.data?.find((p) => p.provider === i?.provider)

  if (!canRead) return <Forbidden what="integrations" />

  return (
    <div className="space-y-4">
      <SectionHeader
        title={i?.displayName ?? 'Integration'}
        description={
          i
            ? `${i.provider} · ${CATEGORY_LABEL[i.category] ?? i.category} · ${i.environment}`
            : 'Loading…'
        }
        right={
          <Link to="/integrations" className="erp-btn">
            <ArrowLeft className="h-3.5 w-3.5" /> Registry
          </Link>
        }
      />

      <ErrorStrip message={detail.error} />

      {/* The same warning as the provider page, because this is the other door into
          the same wrong conclusion: credentials stored here, no calls in the log, and
          no email — with nothing on screen connecting the three. */}
      {i && PROVIDER_NOT_WIRED[i.provider] && (
        <Note tone="bad">
          <div className="space-y-1.5">
            <div>
              <strong>Nothing reads these credentials.</strong> {providerLabel(i.provider)} is
              driven by {PROVIDER_NOT_WIRED[i.provider]!.where} — not by this registration.
            </div>
            <div>
              That is also why the Calls tab is empty: these calls never pass through the
              integration layer. Delivery evidence lives in{' '}
              <Link className="underline" to="/notifications?failedOnly=true">
                the {PROVIDER_NOT_WIRED[i.provider]!.evidence}
              </Link>
              .
            </div>
          </div>
        </Note>
      )}

      <div className="flex items-center gap-0.5 border-b border-border overflow-x-auto">
        {TABS.map((t) => (
          <button
            key={t.id}
            type="button"
            onClick={() => setTab(t.id)}
            className={cn(
              'px-3 py-2 text-erp-sm border-b-2 -mb-px inline-flex items-center gap-1.5 whitespace-nowrap',
              tab === t.id
                ? 'border-chocolate text-chocolate font-medium'
                : 'border-transparent text-muted-foreground hover:text-foreground',
            )}
          >
            <t.icon className="h-3.5 w-3.5" />
            {t.label}
          </button>
        ))}
      </div>

      {tab === 'overview' && (
        <OverviewTab
          id={id}
          detail={detail}
          descriptor={descriptor}
          canUpdate={canUpdate}
        />
      )}
      {tab === 'credentials' && (
        <CredentialsTab id={id} canCredential={canCredential} authKind={i?.authKind} descriptor={descriptor} />
      )}
      {tab === 'report' && <ReportTab id={id} />}
      {tab === 'calls' && <CallsTab id={id} />}
    </div>
  )
}

// ---------------------------------------------------------------------------
// Overview — status, health, and the config allowlist
// ---------------------------------------------------------------------------

function OverviewTab({
  id, detail, descriptor, canUpdate,
}: {
  id: string
  detail: ReturnType<typeof useLoad<Awaited<ReturnType<typeof getIntegration>> | null>>
  descriptor: ProviderResponse | undefined
  canUpdate: boolean
}) {
  const [busy, setBusy] = useState<string | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [health, setHealth] = useState<HealthCheckResponse | null>(null)

  const i = detail.data
  if (detail.loading && !i) return <Panel><LoadingRows colSpan={1} /></Panel>
  if (!i) return <Panel><EmptyState title="Not found" hint="This integration no longer exists." /></Panel>

  const run = async (key: string, fn: () => Promise<unknown>) => {
    setBusy(key)
    setError(null)
    try {
      const r = await fn()
      if (key === 'health') setHealth(r as HealthCheckResponse)
      await detail.refresh()
    } catch (e) {
      setError(explain(e))
    } finally {
      setBusy(null)
    }
  }

  return (
    <div className="space-y-3">
      <ErrorStrip message={error} />

      <KpiStrip>
        <MetricCard label="Status" value={humanise(i.status)} accent={i.status === 'ACTIVE'} />
        <MetricCard label="Health" value={humanise(i.health)} sub={`${i.consecutiveFailures} consecutive failures`} />
        <MetricCard label="Quota today" value={fmtNumber(i.quotaUsedToday)} sub={`${fmtNumber(i.requestsToday)} requests today`} />
        <MetricCard label="Environment" value={i.environment} />
      </KpiStrip>

      <div className="grid gap-3 lg:grid-cols-2">
        <Panel
          title="Registration"
          right={
            canUpdate && (
              <div className="flex gap-1.5">
                <EditRegistrationButton
                  integration={i}
                  descriptor={descriptor}
                  onSaved={() => void detail.refresh()}
                />
                <button
                  type="button"
                  className="erp-btn"
                  disabled={busy !== null}
                  onClick={() => void run('health', () => runHealthCheck(id))}
                >
                  {busy === 'health' ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Activity className="h-3.5 w-3.5" />}
                  Health check
                </button>
                <button
                  type="button"
                  className="erp-btn"
                  disabled={busy !== null}
                  onClick={() =>
                    void run('toggle', () => (i.status === 'ACTIVE' ? disableIntegration(id) : enableIntegration(id)))
                  }
                >
                  {i.status === 'ACTIVE' ? 'Disable' : 'Enable'}
                </button>
              </div>
            )
          }
        >
          <dl className="space-y-0">
            <Row label="Code" value={<code className="font-mono text-erp-xs">{i.code}</code>} />
            <Row label="Provider" value={i.provider} />
            <Row label="Auth" value={AUTH_KIND_LABEL[i.authKind] ?? i.authKind} />
            <Row label="Base URL" value={i.baseUrl ? <code className="font-mono text-erp-xs break-all">{i.baseUrl}</code> : '—'} />
            <Row label="Capabilities" value={i.capabilities?.length ? i.capabilities.join(', ') : '—'} />
            <Row label="Scope" value={i.scopeUnitId ? 'Scoped to a unit' : 'National'} />
          </dl>

          {canUpdate && (
            <div className="mt-3">
              <EditUnavailableNote integration={i} />
            </div>
          )}

          {health && (
            <div className="mt-3">
              <Note tone={health.ok ? 'good' : 'bad'}>
                {/*
                  The detail is the useful part and the backend now always sends
                  one — including on success, where it names the status code that
                  counted as reachable. Reporting only "Failed" with no reason is
                  what turned an ordinary 302 on eCitizen's root into an
                  unexplained red cross beside a lookup that was working.
                */}
                <div>
                  {health.ok ? 'Reachable' : 'Not reachable'}
                  {health.latencyMs != null && ` — ${health.latencyMs} ms`}
                  {health.statusCode != null && `, HTTP ${health.statusCode}`}
                </div>
                {health.detail && (
                  <div className="text-erp-xs opacity-90 mt-0.5">{health.detail}</div>
                )}
                <div className="text-erp-xs opacity-75 mt-1">
                  This probe requests the root of the base URL, unauthenticated. It says the host
                  answers — not that your credentials work. To test those, run a real lookup and
                  read the Calls tab.
                </div>
              </Note>
            </div>
          )}
        </Panel>

        <ConfigPanel
          config={i.config ?? {}}
          allowedKeys={descriptor?.configKeys ?? []}
          editable={i.version != null}
        />
      </div>
    </div>
  )
}

function Row({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <div className="flex items-start justify-between gap-3 py-1.5 border-b border-border last:border-0">
      <dt className="text-erp-sm text-muted-foreground shrink-0">{label}</dt>
      <dd className="text-erp-sm text-right min-w-0">{value}</dd>
    </div>
  )
}

/**
 * Configuration — the declared keys and their current values.
 *
 * Editing happens in one place, the Edit button on the Registration panel, not
 * here: base URL, display name and the config keys are all the same PATCH with
 * the same `If-Match` version, and splitting them across two forms would mean
 * two chances to lose an optimistic lock for one logical correction.
 *
 * This panel is where you *read* the configuration, and it stays a panel rather
 * than becoming a form for that reason.
 */
function ConfigPanel({
  config, allowedKeys, editable,
}: {
  config: Record<string, unknown>
  allowedKeys: string[]
  editable: boolean
}) {
  return (
    <Panel
      title="Configuration"
      description="Non-secret settings. Credentials live on their own tab."
    >
      {allowedKeys.length === 0 ? (
        <p className="text-erp-sm text-muted-foreground py-2">
          This provider declares no configuration keys — there is nothing to set here.
        </p>
      ) : (
        <>
          <dl className="space-y-0">
            {allowedKeys.map((k) => (
              <Row
                key={k}
                label={k}
                value={
                  config[k] == null || config[k] === ''
                    ? <span className="text-muted-foreground">Not set</span>
                    : <code className="font-mono text-erp-xs break-all">{String(config[k])}</code>
                }
              />
            ))}
          </dl>
          <p className="text-erp-xs text-muted-foreground mt-3">
            {editable
              ? 'Edit these on the Registration panel — they save together with the base URL, under one version check. Only these keys are ever accepted; anything secret-shaped is rejected server-side.'
              : 'Editing is not available on this backend — see the note on the Registration panel. Only these keys are ever accepted; anything secret-shaped is rejected server-side.'}
          </p>
        </>
      )}
    </Panel>
  )
}

// ---------------------------------------------------------------------------
// Credentials — the rotation lifecycle
// ---------------------------------------------------------------------------

function CredentialsTab({
  id, canCredential, authKind, descriptor,
}: {
  id: string
  canCredential: boolean
  authKind?: string
  descriptor: ProviderResponse | undefined
}) {
  const creds = useLoad(() => listCredentials(id), [id])
  const [busy, setBusy] = useState<string | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [result, setResult] = useState<string | null>(null)
  const [addOpen, setAddOpen] = useState(false)

  const isOAuth = authKind === 'OAUTH2_CLIENT_CREDENTIALS' || authKind === 'OAUTH2_AUTH_CODE'

  const run = async (key: string, fn: () => Promise<unknown>) => {
    setBusy(key)
    setError(null)
    setResult(null)
    try {
      const r = await fn()
      if (key.startsWith('test:')) {
        const h = r as HealthCheckResponse
        setResult(h.ok ? `Provider answered in ${h.latencyMs ?? '—'} ms.` : `Failed — ${h.detail ?? 'no detail'}.`)
      }
      if (key === 'decrypt') {
        const d = r as { decryptable: boolean; resolved: number }
        setResult(
          d.decryptable
            ? `All ${d.resolved} credential(s) decrypt. This does not mean the provider accepts them.`
            : 'At least one credential could not be decrypted.',
        )
      }
      await creds.refresh()
    } catch (e) {
      setError(explain(e))
    } finally {
      setBusy(null)
    }
  }

  const rows = creds.data ?? []

  return (
    <div className="space-y-3">
      <ErrorStrip message={error} />

      <Note tone="info">
        A credential is stored once and never returned. What you see is a fingerprint, a status and
        an expiry — enough to answer &ldquo;is this the key I think it is&rdquo; without anyone being
        able to walk away with it. To change a key, store the new one and promote it; both work until
        you do.
      </Note>

      {isOAuth && (
        <KnownIssue>
          <strong>Testing a specific credential is unreliable for OAuth2 providers.</strong> The
          backend caches the access token per integration rather than per credential version, so a
          test of a not-yet-promoted secret is made with the currently active one and comes back
          green either way. Promote, then test.
        </KnownIssue>
      )}

      <Panel
        title="Credentials"
        description={
          descriptor?.requiredCredentials?.length
            ? `Required: ${descriptor.requiredCredentials.map((k) => CREDENTIAL_KIND_LABEL[k] ?? k).join(', ')}`
            : undefined
        }
        right={
          canCredential && (
            <div className="flex gap-1.5">
              <button
                type="button"
                className="erp-btn"
                disabled={busy !== null}
                onClick={() => void run('decrypt', () => testCredentialsLocally(id))}
              >
                {busy === 'decrypt' ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <ShieldCheck className="h-3.5 w-3.5" />}
                Check decryption
              </button>
              <button type="button" className="erp-btn erp-btn-primary" onClick={() => setAddOpen(true)}>
                <Plus className="h-3.5 w-3.5" /> Store / rotate
              </button>
            </div>
          )
        }
        bodyClassName="p-0"
      >
        {result && <div className="px-4 py-2 text-erp-sm border-b border-border">{result}</div>}
        <ErpTable head={<>
          <Th>Kind</Th>
          <Th>Version</Th>
          <Th className="axis-hide-md">Fingerprint</Th>
          <Th>Status</Th>
          <Th className="axis-hide-lg">Expires</Th>
          <Th />
        </>}>
          {creds.loading && <LoadingRows colSpan={6} />}
          {!creds.loading && rows.length === 0 && (
            <tr>
              <td colSpan={6} className="px-4 py-10 text-center text-erp-sm text-muted-foreground">
                No credentials stored. This integration cannot authenticate until one is.
              </td>
            </tr>
          )}
          {rows.map((c: CredentialResponse) => (
            <tr key={c.id} className="hover:bg-muted/40">
              <Td className="font-medium">{CREDENTIAL_KIND_LABEL[c.credentialKind] ?? c.credentialKind}</Td>
              <Td className="tabular-nums">v{c.versionNo}</Td>
              <Td className="axis-hide-md font-mono text-erp-xs text-muted-foreground">{c.fingerprint}</Td>
              <Td>
                <span className={cn('text-erp-xs font-medium', CRED_STATUS_STYLE[c.status])}>
                  {humanise(c.status)}
                </span>
              </Td>
              <Td className="axis-hide-lg text-erp-xs">{fmtDateTime(c.expiresAt) || '—'}</Td>
              <Td className="text-right">
                {canCredential && (
                  <div className="inline-flex gap-1.5">
                    <button
                      type="button"
                      className="erp-btn"
                      disabled={busy !== null}
                      onClick={() => void run(`test:${c.id}`, () => testCredential(id, c.id))}
                    >
                      Test
                    </button>
                    {c.status === 'ROTATING' && (
                      <button
                        type="button"
                        className="erp-btn"
                        disabled={busy !== null}
                        onClick={() => void run('promote', () => promoteCredential(id, c.id))}
                      >
                        Promote
                      </button>
                    )}
                    {c.status !== 'REVOKED' && (
                      <button
                        type="button"
                        className="erp-btn"
                        disabled={busy !== null}
                        onClick={() => {
                          const reason = window.prompt('Why is this credential being revoked?')
                          if (reason) void run('revoke', () => revokeCredential(id, c.id, reason))
                        }}
                      >
                        Revoke
                      </button>
                    )}
                  </div>
                )}
              </Td>
            </tr>
          ))}
        </ErpTable>
      </Panel>

      {addOpen && (
        <AddCredentialModal
          id={id}
          kinds={[...(descriptor?.requiredCredentials ?? []), ...(descriptor?.optionalCredentials ?? [])]}
          onClose={() => setAddOpen(false)}
          onSaved={() => { setAddOpen(false); void creds.refresh() }}
        />
      )}
    </div>
  )
}

function AddCredentialModal({
  id, kinds, onClose, onSaved,
}: {
  id: string
  kinds: string[]
  onClose: () => void
  onSaved: () => void
}) {
  const [kind, setKind] = useState(kinds[0] ?? 'API_KEY')
  const [value, setValue] = useState('')
  const [expiresAt, setExpiresAt] = useState('')
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState<string | null>(null)

  return (
    <Modal
      open
      title="Store or rotate a credential"
      description="The value travels once, in this request, and is never returned. Storing a kind that already exists creates the next version in ROTATING — both work until you promote."
      onClose={onClose}
      footer={
        <>
          <button type="button" className="erp-btn" onClick={onClose} disabled={saving}>Cancel</button>
          <button
            type="button"
            className="erp-btn erp-btn-primary"
            disabled={!value.trim() || saving}
            onClick={async () => {
              setSaving(true)
              setError(null)
              try {
                await putCredential(id, {
                  // `credentialKind`, not `kindCode` — CreateCredentialRequest is
                  // @NotBlank on this name, so the wrong key is a silent 400.
                  credentialKind: kind,
                  value: value.trim(),
                  expiresAt: expiresAt ? new Date(`${expiresAt}T00:00:00Z`).toISOString() : null,
                })
                onSaved()
              } catch (e) {
                setError(explain(e))
              } finally {
                setSaving(false)
              }
            }}
          >
            {saving && <Loader2 className="h-3.5 w-3.5 animate-spin" />} Store
          </button>
        </>
      }
    >
      <ErrorStrip message={error} />
      <label className="block">
        <span className="text-erp-xs text-muted-foreground">Kind</span>
        <select className="erp-input w-full mt-1" value={kind} onChange={(e) => setKind(e.target.value)}>
          {(kinds.length ? kinds : ['API_KEY']).map((k) => (
            <option key={k} value={k}>{CREDENTIAL_KIND_LABEL[k] ?? k}</option>
          ))}
        </select>
      </label>
      <label className="block mt-3">
        <span className="text-erp-xs text-muted-foreground">Value</span>
        <input
          type="password"
          autoComplete="off"
          className="erp-input w-full mt-1 font-mono"
          value={value}
          onChange={(e) => setValue(e.target.value)}
          placeholder="Paste the secret"
        />
      </label>
      <label className="block mt-3">
        <span className="text-erp-xs text-muted-foreground">Expires (optional)</span>
        <DatePicker value={expiresAt} onChange={setExpiresAt} className="w-full mt-1" />
      </label>
    </Modal>
  )
}

// ---------------------------------------------------------------------------
// Report and calls
// ---------------------------------------------------------------------------

function ReportTab({ id }: { id: string }) {
  const [range, setRange] = useState<RangeId>('7d')
  const report = useLoad(() => integrationReport(id, rangeParams(range)), [id, range])
  const model = report.data ? integrationReportModel(report.data) : null

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between gap-2">
        <div className="text-erp-sm text-muted-foreground">
          Every call this integration made, as recorded at the moment it was made.
        </div>
        <RangePicker value={range} onChange={setRange} onRefresh={() => void report.refresh()} busy={report.loading} />
      </div>

      {/*
        This banner used to say the failure was a jsonb operator and affected
        only eCitizen. That was wrong, and wrong in the expensive direction: it
        sent people looking at the facet query while the method was actually
        dying four queries earlier, for every provider. The real cause is
        `java.time.Instant` handed to `JdbcTemplate` — pgJDBC binds a timestamptz
        from an OffsetDateTime and refuses to guess an offset. Never narrow a
        failure's blast radius in copy on the strength of a plausible theory;
        the log said "for all integrations" and the UI said otherwise.
      */}
      {report.error && (
        <KnownIssue>
          <strong>Reporting is down for every integration, not just this one.</strong> The backend
          passes the window bounds to a hand-written JDBC query as{' '}
          <code className="font-mono">java.time.Instant</code>, and PostgreSQL&rsquo;s driver cannot
          bind one — a <code className="font-mono">timestamptz</code> needs an offset. It fails on
          the first query, so nothing on this tab can load, and the same fault takes out the calls
          list, the API-key and webhook reports, and the nightly rollup. Prompt 26 §1 fixes all six
          sites.
        </KnownIssue>
      )}

      <ReportBody
        model={model}
        error={report.error}
        loading={report.loading}
        emptyHint="Either this integration has not been called in this window, or its calls are not reaching the telemetry table. Check the Calls tab before concluding it is idle."
      />
    </div>
  )
}

function CallsTab({ id }: { id: string }) {
  const [outcome, setOutcome] = useState('')
  /** Which row is open. One at a time: this is a reading pane, not a diff. */
  const [open, setOpen] = useState<string | null>(null)
  const [cursor, setCursor] = useState<string | undefined>(undefined)
  const [stack, setStack] = useState<string[]>([])

  const page = useLoad(
    () => integrationCalls(id, { outcome: outcome || undefined, cursor, size: 50 }),
    [id, outcome, cursor],
  )
  const rows = page.data?.content ?? []

  return (
    <div className="space-y-3">
      <Panel bodyClassName="p-0">
        <div className="px-4 py-2.5 border-b border-border flex items-center gap-2">
          <span className="text-erp-xs text-muted-foreground">Outcome</span>
          <select
            className="erp-input h-8"
            value={outcome}
            onChange={(e) => { setOutcome(e.target.value); setCursor(undefined); setStack([]) }}
          >
            <option value="">All</option>
            {['SUCCESS', 'NOT_FOUND', 'CLIENT_ERROR', 'SERVER_ERROR', 'TIMEOUT', 'RATE_LIMITED', 'CIRCUIT_OPEN', 'TRANSPORT_ERROR'].map((o) => (
              <option key={o} value={o}>{humanise(o)}</option>
            ))}
          </select>
          <span className="text-erp-xs text-muted-foreground ml-auto">
            Provider explanations are recorded. Subject values, URLs and query strings are not.
          </span>
        </div>

        <ErpTable head={<>
          <Th>When</Th>
          <Th>Capability</Th>
          <Th>Outcome</Th>
          <Th className="axis-hide-md">Status</Th>
          <Th className="axis-hide-md">Latency</Th>
          <Th className="axis-hide-lg">Facets</Th>
          <Th className="w-8"><span className="sr-only">Detail</span></Th>
        </>}>
          {page.loading && <LoadingRows colSpan={7} />}
          {!page.loading && rows.length === 0 && (
            <tr>
              <td colSpan={7} className="px-4 py-10 text-center text-erp-sm text-muted-foreground">
                No calls recorded in this window.
              </td>
            </tr>
          )}
          {rows.map((c: IntegrationCallResponse) => [
            <tr key={c.id}
                className={cn('hover:bg-muted/40', hasDetail(c) && 'cursor-pointer')}
                onClick={() => hasDetail(c) && setOpen(open === c.id ? null : c.id)}>
              <Td className="text-erp-xs whitespace-nowrap">{fmtDateTime(c.calledAt)}</Td>
              <Td className="font-mono text-erp-xs">{c.capability}</Td>
              <Td>
                <span className="inline-flex items-center gap-1.5 text-erp-xs">
                  <span className={cn(
                    'h-1.5 w-1.5 rounded-full',
                    outcomeTone(c.outcome) === 'good' && 'bg-emerald-600',
                    outcomeTone(c.outcome) === 'warn' && 'bg-amber-500',
                    outcomeTone(c.outcome) === 'bad' && 'bg-red-600',
                    !outcomeTone(c.outcome) && 'bg-muted-foreground',
                  )} />
                  {humanise(c.outcome)}
                </span>
              </Td>
              <Td className="axis-hide-md tabular-nums text-erp-xs">
                {c.httpStatus ?? <span className="text-muted-foreground" title="Never reached the wire">—</span>}
              </Td>
              <Td className="axis-hide-md tabular-nums text-erp-xs">{c.latencyMs} ms</Td>
              <Td className="axis-hide-lg text-erp-xs text-muted-foreground truncate max-w-[16rem]">
                {c.dimensions && Object.keys(c.dimensions).length
                  ? Object.entries(c.dimensions).map(([k, v]) => `${k}=${v}`).join(' · ')
                  : '—'}
              </Td>
              <Td className="text-erp-xs text-muted-foreground">
                {hasDetail(c) ? (open === c.id ? '−' : '+') : ''}
              </Td>
            </tr>,
            open === c.id && hasDetail(c) ? (
              <tr key={`${c.id}-detail`} className="bg-muted/30">
                <td colSpan={7} className="px-4 py-3">
                  <CallDetail call={c} />
                </td>
              </tr>
            ) : null,
          ])}
        </ErpTable>
      </Panel>

      <CursorPager
        page={stack.length}
        count={rows.length}
        hasMore={page.data?.hasMore ?? false}
        canGoBack={stack.length > 0}
        loading={page.loading}
        unit="calls"
        onNext={() => {
          const next = page.data?.nextCursor
          if (!next) return
          setStack((s) => [...s, cursor ?? ''])
          setCursor(next)
        }}
        onBack={() => {
          // Pop outside the updater — state updaters must be pure, and under
          // StrictMode's double-invocation a setState inside one runs twice.
          const copy = [...stack]
          const prev = copy.pop()
          setStack(copy)
          setCursor(prev || undefined)
        }}
      />
    </div>
  )
}

/** Is there anything behind this row worth opening? */
function hasDetail(c: IntegrationCallResponse): boolean {
  return Boolean(c.errorMessage || c.errorCode || c.requestId || c.upstreamRef)
}

/**
 * What the provider actually said.
 *
 * <p>This pane exists because its absence cost a real afternoon. A watch polling X failed every
 * five minutes for hours; the Calls tab showed `CLIENT_ERROR · 400` and nothing else, and the
 * only way to learn that X was rejecting the query was to read the driver's source. A status
 * code says a call failed. It never says what to change.</p>
 *
 * <p>Explanation fields only — the raw body is deliberately not stored, because these APIs echo
 * the request that caused the error and for a registry lookup that echo is somebody's ID
 * number.</p>
 */
function CallDetail({ call }: { call: IntegrationCallResponse }) {
  return (
    <div className="space-y-2">
      {call.errorMessage ? (
        <div>
          <p className="text-erp-xs uppercase tracking-wide text-muted-foreground mb-1">
            What {call.direction === 'INBOUND' ? 'the caller' : 'the provider'} said
          </p>
          <p className="font-mono text-erp-xs leading-relaxed whitespace-pre-wrap break-words">
            {call.errorMessage}
          </p>
        </div>
      ) : (
        <p className="text-erp-xs text-muted-foreground">
          No explanation was returned with this call.
        </p>
      )}

      <dl className="flex flex-wrap gap-x-6 gap-y-1 pt-1">
        {([
          ['Error code', call.errorCode],
          ['Request id', call.requestId],
          ['Upstream ref', call.upstreamRef],
          ['Attempt', call.attempt > 1 ? String(call.attempt) : null],
        ] as [string, string | null | undefined][])
          .filter(([, v]) => v)
          .map(([k, v]) => (
            <div key={k} className="flex items-baseline gap-1.5">
              <dt className="text-erp-xs text-muted-foreground">{k}</dt>
              <dd className="font-mono text-erp-xs">{v}</dd>
            </div>
          ))}
      </dl>
    </div>
  )
}
